import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { contactInfo, siteIntro } from "@/mocks/catalog";

export default function About() {
  usePageMeta(
    "关于我们_豆花视频",
    "了解豆花视频的网站定位、服务范围与联系方式。豆花视频是高清影视内容展示与资讯平台，专注影视资源的整理、筛选与推荐。"
  );

  const [toast, setToast] = useState("");

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2000);
  };

  const copyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      showToast("邮箱已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动记录邮箱地址");
    }
  };

  const features = [
    { icon: "ri-movie-2-line", title: "内容丰富", desc: "覆盖电影、电视剧、综艺等多元影视类型" },
    { icon: "ri-search-eye-line", title: "查找便捷", desc: "支持关键词搜索与多维筛选，快速定位内容" },
    { icon: "ri-information-line", title: "信息详实", desc: "主创阵容、剧情简介、评分等一应俱全" },
    { icon: "ri-smartphone-line", title: "体验流畅", desc: "适配桌面与手机端，浏览顺畅无干扰" },
  ];

  return (
    <div className="w-full">
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-5 text-2xl md:text-4xl font-black text-foreground-950 mb-3">
            关于<span className="text-primary-500">豆花视频</span>
          </h1>
          <p className="text-sm md:text-base text-foreground-500 max-w-3xl leading-relaxed">
            高清影视资源精选与视频内容平台，专注内容展示与资讯导航，让每一次观影选择都更轻松。
          </p>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
              <h2 className="text-lg md:text-xl font-bold text-foreground-950">网站介绍</h2>
            </div>
            <div className="space-y-4">
              {siteIntro.paragraphs.map((p) => (
                <p key={p.slice(0, 12)} className="text-sm md:text-base text-foreground-600 leading-loose">
                  {p}
                </p>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              {features.map((f) => (
                <div key={f.title} className="flex items-start gap-3 p-4 rounded-lg border border-background-200 bg-background-50">
                  <span className="w-10 h-10 shrink-0 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center text-xl">
                    <i className={f.icon}></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">{f.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
                <h2 className="text-lg md:text-xl font-bold text-foreground-950">版权与免责声明</h2>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100 p-5 space-y-3 text-sm text-foreground-600 leading-loose">
                <p>本站为影视内容展示与资讯平台，所有影片信息均来源于公开资料，仅供用户浏览与参考，不提供任何在线播放、下载或存储服务。</p>
                <p>本站不对影视资源的版权归属作出主张，如相关内容涉及版权问题，请通过下方联系方式与我们联系，我们将及时处理。</p>
                <p>站内展示的数据为演示示例，用于展示页面的内容组织与交互效果。</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 lg:sticky lg:top-20">
              <h2 className="text-base font-bold text-foreground-950 mb-5 flex items-center gap-2">
                <i className="ri-customer-service-2-line text-primary-500"></i>
                联系方式
              </h2>
              <ul className="space-y-4 text-sm">
                <li>
                  <p className="text-xs text-foreground-400 mb-1">客服邮箱</p>
                  <div className="flex items-center justify-between gap-2">
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="text-foreground-800 hover:text-primary-600 transition-colors break-all"
                    >
                      {contactInfo.email}
                    </a>
                    <button
                      onClick={() => copyEmail(contactInfo.email)}
                      className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md border border-background-300 text-foreground-500 hover:text-primary-600 hover:border-primary-300 transition-colors cursor-pointer"
                      aria-label="复制客服邮箱"
                    >
                      <i className="ri-file-copy-line"></i>
                    </button>
                  </div>
                </li>
                <li>
                  <p className="text-xs text-foreground-400 mb-1">商务合作</p>
                  <a
                    href={`mailto:${contactInfo.cooperation}`}
                    className="text-foreground-800 hover:text-primary-600 transition-colors break-all"
                  >
                    {contactInfo.cooperation}
                  </a>
                </li>
                <li>
                  <p className="text-xs text-foreground-400 mb-1">联系地址</p>
                  <p className="text-foreground-800 leading-relaxed">{contactInfo.address}</p>
                </li>
                <li>
                  <p className="text-xs text-foreground-400 mb-1">联系电话</p>
                  <p className="text-foreground-800">{contactInfo.phone}</p>
                </li>
              </ul>

              <div className="mt-6 pt-6 border-t border-background-200 flex flex-col gap-3">
                <Link
                  to="/"
                  className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-4-line"></i>
                  返回首页
                </Link>
                <Link
                  to="/search"
                  className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-search-line"></i>
                  搜索影视
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm dh-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}